/**
 * Casting lots rolled up — typed wrapper over `GET /v1/twin/lots/rollup` (platform-core/internal/twin/lot_rollup.go).
 *
 * The org's casting lots BOOKED in a window, summed by period, by part, by shift or line, or in total: lots, pieces cast
 * and scrapped, and scrapped pieces per defect as booked. It serves the reports of a plant that books scrap per
 * production day and part (an imported CMS), where there are no per-casting units to count. Pieces are summed
 * server-side (a rate is scrap over cast, never a mean of rates), and a lot whose outcome is unknown or impossible is
 * left out. A plant that books its lots per shift and line rolls them up by the label each was booked on (`shift`,
 * `line`): the labels in order, and the lots booked with none in one row keyed "" after them.
 */

import { api } from "@/lib/api-client";
import { qs } from "./qs";

export type LotRollupBy = "day" | "week" | "month" | "part" | "shift" | "line" | "all";

export interface LotRollupRow {
  /**
   * The period's key (2026-08, 2026-W32, 2026-08-03), the part, or the shift or line the lots were booked on; "" for
   * the total, and for the lots booked with no part, shift or line.
   */
  key: string;
  /** A period's first plant day (YYYY-MM-DD); absent for a part, a shift, a line or the total. */
  first_day?: string;
  lots: number;
  cast: number;
  scrap: number;
  /** scrap / cast; null when nothing was cast. */
  scrap_rate: number | null;
  /** Scrapped pieces per defect as booked (canonical names, e.g. "cold_iron"). */
  defects: Record<string, number>;
}

export interface LotRollup {
  by: LotRollupBy;
  /** Periods newest first; parts most scrapped first; shifts and lines by label, none last; empty for `all`. */
  rows: LotRollupRow[];
  totals: LotRollupRow;
}

export interface LotRollupParams {
  by?: LotRollupBy;
  /** A date (YYYY-MM-DD) or an RFC 3339 instant; empty or absent = no bound. */
  from?: string;
  /** A date (inclusive) or an RFC 3339 instant; empty or absent = no bound. */
  to?: string;
}

/** The route's path for a rollup, with no bound where none is given. */
export function lotRollupPath(params: LotRollupParams = {}): string {
  return `/v1/twin/lots/rollup${qs({ by: params.by, from: params.from || undefined, to: params.to || undefined })}`;
}

export const twinLotRollup = {
  get: (params: LotRollupParams = {}) => api.get<LotRollup>(lotRollupPath(params)),
};
